import { Dialog } from '@base-ui/react/dialog';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, PanelLeft, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { AppShell } from '@/shared/layout/app-shell';
import { cn } from '@/shared/lib/cn';
import { useResizableWidth } from '@/shared/lib/use-resizable-width';
import { useStudio, type StudioModel } from '@/shared/program/use-studio';
import { activityHref } from '@/shared/ui/activity/activity-kind';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { PageSkeleton } from '@/shared/ui/skeleton';

import { StudioNav } from './studio-nav';

import './studio-layout.css';

import type { ReactNode } from 'react';

export function StudioLayout({
  activeId,
  subtitle,
  scrollKey,
  showExplorer = true,
  contentMode = 'scroll',
  preserveContent = false,
  toolbar,
  context,
  headerActions,
  content,
  visible = true,
  children,
}: {
  activeId: string | null;
  subtitle?: string | undefined;
  scrollKey?: string;
  showExplorer?: boolean;
  contentMode?: 'scroll' | 'workspace';
  /** Keep an editor's DOM identity across activity selection and curriculum refreshes. */
  preserveContent?: boolean;
  toolbar?: ReactNode;
  context?: ReactNode;
  headerActions?: ReactNode;
  /** Persistent content can render while curriculum data is unavailable. */
  content?: ReactNode;
  visible?: boolean;
  children?: (studio: StudioModel) => ReactNode;
}) {
  const { data, isPending, error, refetch, isFetching } = useStudio();
  const [mobileOpen, setMobileOpen] = useState(false);
  const explorerId = useId();
  const mobileExplorerId = useId();
  useEffect(() => {
    if (!visible) setMobileOpen(false);
  }, [visible]);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('mio.curriculum-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const resizable = useResizableWidth({
    storageKey: 'mio.curriculum-width',
    defaultWidth: 280,
    min: 220,
    max: 420,
    reserve: 860,
    edge: 'right',
  });
  const index = data?.sequence.findIndex((item) => item.id === activeId) ?? -1;
  const current = data?.sequence[index];
  const previous = index > 0 ? data?.sequence[index - 1] : undefined;
  const next = index >= 0 ? data?.sequence[index + 1] : undefined;
  function toggleExplorer() {
    setCollapsed(!collapsed);
    try {
      localStorage.setItem('mio.curriculum-collapsed', String(!collapsed));
    } catch {
      /* The current view still works without storage. */
    }
  }
  return (
    <AppShell
      title="Studio"
      subtitle={subtitle}
      frame="full"
      compact
      visible={visible}
      contentId={preserveContent ? 'studio-workshop-content' : 'studio-content'}
    >
      {showExplorer && !collapsed && data ? (
        <aside
          id={explorerId}
          aria-label="Curriculum explorer"
          className="border-line relative hidden h-full shrink-0 border-r md:block"
          style={{ width: resizable.width }}
        >
          <StudioNav
            studio={data}
            activeId={activeId}
            onHide={toggleExplorer}
            explorerId={explorerId}
          />
          <div
            role="separator"
            aria-label="Resize curriculum"
            aria-orientation="vertical"
            aria-valuenow={resizable.width}
            aria-valuemin={resizable.min}
            aria-valuemax={resizable.max}
            tabIndex={0}
            title="Drag to resize curriculum. Double-click to reset."
            className="hover:bg-accent-line focus-visible:bg-accent-line absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize touch-none"
            {...resizable.handleProps}
          />
        </aside>
      ) : null}
      <div className="studio-content-pane flex min-h-0 min-w-0 flex-1 flex-col">
        {showExplorer ? (
          <div className="studio-workspace-toolbar">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileOpen(true)}
              className="md:hidden"
              aria-expanded={mobileOpen}
              aria-controls={mobileExplorerId}
            >
              <PanelLeft className="size-4" />
              Curriculum
            </Button>
            {collapsed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleExplorer}
                className="hidden md:inline-flex"
                aria-expanded={false}
                aria-controls={explorerId}
                aria-label="Show curriculum"
              >
                <PanelLeft className="size-4" />
                <span className="hidden xl:inline">Curriculum</span>
              </Button>
            ) : null}
            <nav
              aria-label="Lesson location"
              className="studio-workspace-location text-fg-muted min-w-0 text-xs leading-5"
            >
              {context ??
                (current ? (
                  <>
                    <Link
                      to="/studio/modules/$number"
                      params={{ number: String(current.moduleNumber) }}
                      className="hover:text-accent"
                    >
                      Module {current.moduleNumber}
                    </Link>
                    <span className="mx-2" aria-hidden="true">
                      /
                    </span>
                    <span className="text-fg" title={current.title}>
                      {current.title}
                    </span>
                  </>
                ) : (
                  (subtitle ?? 'Curriculum overview')
                ))}
            </nav>
            {toolbar ? <div className="studio-workspace-views">{toolbar}</div> : null}
            <div className="studio-workspace-actions flex shrink-0 items-center gap-1">
              {headerActions ?? (
                <>
                  {previous ? (
                    <Link
                      to={activityHref(previous.kind, previous.id)}
                      className="text-fg-muted hover:bg-surface rounded-md p-2"
                      aria-label={`Previous: ${previous.title}`}
                      title={`Previous: ${previous.title}`}
                    >
                      <ArrowLeft className="size-4" />
                    </Link>
                  ) : null}
                  {next ? (
                    <Link
                      to={activityHref(next.kind, next.id)}
                      className="text-fg-muted hover:bg-surface rounded-md p-2"
                      aria-label={`Next: ${next.title}`}
                      title={`Next: ${next.title}`}
                    >
                      <ArrowRight className="size-4" />
                    </Link>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
        {error ? (
          <div className="p-4">
            <Alert>
              {data
                ? 'Could not refresh your progress. Your last loaded curriculum is shown.'
                : `Could not load the curriculum. ${error.message}`}
            </Alert>
            <Button variant="ghost" size="sm" loading={isFetching} onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : null}
        <section
          key={preserveContent ? 'workspace' : (scrollKey ?? activeId)}
          data-studio-scroll
          className={cn(
            'min-h-0 min-w-0 flex-1',
            contentMode === 'workspace'
              ? 'flex flex-col overflow-hidden'
              : 'overflow-y-auto overscroll-contain',
          )}
        >
          {content ??
            (data ? (
              children?.(data)
            ) : isPending ? (
              <PageSkeleton label="Loading Studio…" reader={Boolean(activeId)} />
            ) : null)}
        </section>
      </div>
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-[color-mix(in_oklab,var(--fg)_28%,transparent)]" />
          <Dialog.Popup
            id={mobileExplorerId}
            className="border-line bg-canvas text-fg fixed inset-y-3 left-3 z-50 flex w-[min(360px,calc(100vw-24px))] flex-col overflow-hidden rounded-xl border shadow-xl outline-none"
          >
            <header className="border-line flex items-center justify-between border-b px-4 py-3">
              <Dialog.Title className="text-sm font-semibold">Curriculum</Dialog.Title>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
                aria-label="Close curriculum"
              >
                <X className="size-4" />
              </Button>
            </header>
            <Dialog.Description className="sr-only">
              Browse modules and activities.
            </Dialog.Description>
            <div className="min-h-0 flex-1">
              {data ? (
                <StudioNav
                  studio={data}
                  activeId={activeId}
                  onNavigate={() => setMobileOpen(false)}
                />
              ) : (
                <p className="text-fg-muted p-4 text-sm">
                  The curriculum is loading. Close this panel to keep working.
                </p>
              )}
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </AppShell>
  );
}
